/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import type { BufferedChangeset } from "ember-changeset/types";
import type { IntlService } from "ember-intl";
import CrudJournalEventBaseComponent from "parog-web/components/forc/abstract/crud-journal-event-based-component";
import type { ToastMessage } from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import CapillarityBloodGlucoseMeasuredJournalEvent from "parog-web/models/capillarity-blood-glucose-measured-journal-event";
import type SessionService from "parog-web/services/session";

export default class CapillarityBloodGlucoseEditionFormComponent extends CrudJournalEventBaseComponent {
  @service declare session: SessionService;
  @service declare intl: IntlService;

  static forcDisplayName = "Ma glycémie";
  static fullComponentId = "capillarity-blood-glucose/edition-form";

  inputStyles = "!mt-[-12px]";
  unitTextStyles = "!pt-[32px]";

  get validations(): Record<string, unknown> {
    return this.journalEventClass.validations(this.intl);
  }

  static journalEventClass = CapillarityBloodGlucoseMeasuredJournalEvent;
  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  get model(): typeof this.journalEventClass {
    // eslint-disable-next-line parog-web/no-use-of-model-in-crud-forc
    const model = super.model;

    if (this.isNewMode) {
      model.occurredAt = new Date();
      model.capillarityBloodGlucose = null;
      model.measurementFasting = "other";
    }

    return model;
  }

  get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: "Glycémie enregistrée. Merci !",
          title: "Glycémie"
        }
      : super.toastMessage;
  }

  get displayInsulinQuestions(): boolean {
    const insulin = this.changeset.get("insulin");
    return insulin == "yes" || insulin == "dontKnow";
  }

  get changeset(): BufferedChangeset {
    const changeset = super.changeset;
    this.bloodGlucoseByGram = this.convertToGram(
      this.changeset.get("capillarityBloodGlucose")
    );
    this.bloodGlucoseByMmol = this.changeset.get("capillarityBloodGlucose");
    return changeset;
  }

  @tracked bloodGlucoseByGram;
  @tracked bloodGlucoseByMmol;

  @action
  onBloodGlucoseByGramChanged(e: InputEvent): void {
    this.bloodGlucoseByMmol = "";
    const value = (e.target as HTMLInputElement).value;
    // Set the value in Mmol in the changeset
    this.changeset.set(
      "capillarityBloodGlucose",
      value == "" ? "" : this.convertToMmol(value || "")
    );
  }

  @action
  onBloodGlucoseByMmolChanged(e: InputEvent): void {
    this.bloodGlucoseByGram = "";
    const value = (e.target as HTMLInputElement).value;
    // Set the value in the changeset
    this.changeset.set(
      "capillarityBloodGlucose",
      value == "" ? "" : value || ""
    );
  }

  private convertToGram(value: string): string {
    return this.fixStringValue(
      CapillarityBloodGlucoseMeasuredJournalEvent.convertToGram(
        parseFloat(value)
      )
    );
  }

  private convertToMmol(value: string): string {
    return this.fixStringValue(
      CapillarityBloodGlucoseMeasuredJournalEvent.convertToMmol(
        parseFloat(value)
      )
    );
  }

  private fixStringValue(value: number): string {
    return value?.toFixed(2).replace(/\.?0+$/, "");
  }
}
